'use client';

import { useState, useMemo } from 'react';
import Image from 'next/image';

export default function VideoPlayer({ url, duration, thumbnail, className = '' }) {
  const [isPlaying, setIsPlaying] = useState(false);

  /** -------------------------------------------------
   *  Detect video platform + build embed URL
   * ------------------------------------------------*/
  const videoInfo = useMemo(() => {
    if (!url) {
      return {
        type: 'youtube',
        id: 'dQw4w9WgXcQ',
        base: `https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ`,
      };
    }

    // YouTube (using privacy-enhanced mode to reduce tracking)
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/.*(?:v=|embed\/))([\w-]{11})/);
    if (yt) {
      return {
        type: 'youtube',
        id: yt[1],
        base: `https://www.youtube-nocookie.com/embed/${yt[1]}`,
      };
    }

    // Vimeo
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) {
      return {
        type: 'vimeo',
        id: vm[1],
        base: `https://player.vimeo.com/video/${vm[1]}`,
      };
    }

    // Direct video file
    if (/\.(mp4|webm|ogg)$/i.test(url)) {
      return { type: 'direct', base: url };
    }

    return { type: 'direct', base: url };
  }, [url]);

  /** -------------------------------------------------
   *  Thumbnail
   * ------------------------------------------------*/
  const videoThumbnail = useMemo(() => {
    if (thumbnail) return thumbnail;

    if (videoInfo.type === 'youtube')
      return `https://img.youtube.com/vi/${videoInfo.id}/maxresdefault.jpg`;

    if (videoInfo.type === 'vimeo') return `https://vumbnail.com/${videoInfo.id}.jpg`;

    return 'https://picsum.photos/1920/1080?video';
  }, [thumbnail, videoInfo]);

  /** -------------------------------------------------
   *  Build embed src with params
   * ------------------------------------------------*/
  const buildSrc = (autoplay = false) => {
    if (videoInfo.type === 'direct') return videoInfo.base;

    const params = new URLSearchParams({
      autoplay: autoplay ? 1 : 0,
      controls: 1,
      playsinline: 1,
      rel: 0,
      modestbranding: 1,
      ...(videoInfo.type === 'vimeo' ? { title: 0, byline: 0, portrait: 0, dnt: 1 } : {}),
    });

    return `${videoInfo.base}?${params.toString()}`;
  };

  const embedSrc = buildSrc(isPlaying);

  /** -------------------------------------------------
   *  Render
   * ------------------------------------------------*/
  return (
    <div
      className={`relative w-full aspect-video bg-black rounded-[14px] overflow-hidden shadow-lg ${className}`}
    >
      {!isPlaying && (
        <div className="absolute inset-0 z-10 md:hidden">
          {videoThumbnail && (
            <Image
              src={videoThumbnail}
              alt="thumbnail"
              fill
              className="w-full h-full object-cover"
            />
          )}

          {duration && (
            <span className="absolute bottom-4 left-4 bg-black/80 text-white text-xs px-2 py-1 rounded">
              {duration}
            </span>
          )}

          <button
            onClick={() => setIsPlaying(true)}
            className="absolute bottom-4 right-4 bg-[#00EAFD] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all"
            aria-label="Play"
          >
            <svg
              width="11"
              height="14"
              viewBox="0 0 11 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.4812 5.76971C10.6437 5.87805 10.7656 6.01348 10.8469 6.176C10.9281 6.33851 10.9688 6.51006 10.9688 6.69063C10.9688 6.8712 10.9281 7.04275 10.8469 7.20526C10.7656 7.36778 10.6437 7.50321 10.4812 7.61155L1.65118 13.2183C1.56089 13.2725 1.46609 13.3131 1.36678 13.3402C1.26746 13.3673 1.17266 13.3809 1.08238 13.3809C0.79346 13.3809 0.540658 13.277 0.32397 13.0694C0.107283 12.8617 -0.00106137 12.6044 -0.00106134 12.2974L-0.00106036 1.08384C-0.00106034 0.776862 0.107284 0.519546 0.323971 0.311887C0.540659 0.104228 0.793461 0.0003987 1.08238 0.000398725C1.17266 0.000398733 1.26746 0.0139419 1.36678 0.0410281C1.4661 0.0681134 1.5609 0.108743 1.65118 0.162914L10.4812 5.76971Z"
                fill="#323232"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Unified Player */}
      <div className="w-full h-full">
        {videoInfo.type === 'direct' ? (
          <video className="w-full h-full" controls autoPlay={isPlaying} playsInline>
            <source src={videoInfo.base} type={`video/${videoInfo.base.split('.').pop()}`} />
          </video>
        ) : (
          <iframe
            src={embedSrc}
            className="w-full h-full"
            title="Video player"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          ></iframe>
        )}
      </div>
    </div>
  );
}
